import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';

const TestServices = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize state
  const [resultsState, setResultsState] = useState([]);
  const [historyState, setHistoryState] = useState([]);

  const fetchResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('access');
      const response = await axios.get(`${apiUrl}/accounts/test-services/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setResultsState(response.data.results || []);
      setHistoryState(response.data.history || []);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch test services.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  const StatusIcon = ({ status }) => {
    if (status === 'success') return <CheckCircle className="w-6 h-6 text-green-500" />;
    if (status === 'failed') return <XCircle className="w-6 h-6 text-red-500" />;
    return <Clock className="w-6 h-6 text-yellow-500" />;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f] flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#1e3a5f]" />
            Test Services Connection
          </h1>
          <p className="text-gray-500 mt-1">Verify connectivity with Database, Gemini API, and Google Cloud Storage</p>
        </div>
        <button
          onClick={fetchResults}
          disabled={loading}
          className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2 rounded-lg hover:bg-[#2a4b73] transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Run Tests
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 border border-red-200">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {resultsState.map((result, idx) => (
          <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-semibold text-lg text-[#1e3a5f]">{result.name}</h3>
              <StatusIcon status={result.status} />
            </div>
            <div className="mt-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                ${result.status === 'success' ? 'bg-green-100 text-green-800' : 
                  result.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {result.status}
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-600 break-words">
              {result.details}
            </p>
          </div>
        ))}
        {resultsState.length === 0 && !loading && !error && (
           <div className="col-span-3 text-center text-gray-500 py-8 bg-white rounded-xl shadow-sm border border-gray-100">
             No current results available. Click 'Run Tests' to start.
           </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-[#1e3a5f]">Recent Test History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Service</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {historyState.length > 0 ? historyState.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#1e3a5f]">{item.test_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                      ${item.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate" title={item.details}>
                    {item.details}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleString()}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                    No test history available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TestServices;